/**
 * CelestialContext — Centralized State Management for Calendar / Sky Data
 *
 * Single source of truth for the Enochian calendar across the entire app.
 * One geolocation request, one polled fetch of /api/celestial/data, one
 * shared object that every dashboard, curriculum tab, and AR view consumes.
 *
 * Eliminates the previous fragmentation where 7+ components each fetched
 * /api/celestial/data independently with their own location detection,
 * causing 30-60s drift between widgets and the "Day 18 vs Day 19" parity
 * bugs.
 *
 * Components opt-in via the useCelestial() hook. Hands-off mandate
 * preserved — legacy independent fetches still work; this just unifies
 * everything that consumes the new hook.
 */

import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mississippi River Valley default — matches existing EnochianWatchman default.
const DEFAULT_LOCATION = { lat: 38.6270, lon: -90.1994 };
const DEFAULT_LOCATION_NAME = "St. Louis/Cahokia (Default)";
const POLL_INTERVAL_MS = 60 * 1000; // 1 minute — fine-grained enough for sundown transitions

const CelestialContext = createContext(null);

export function CelestialProvider({ children }) {
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [locationName, setLocationName] = useState(DEFAULT_LOCATION_NAME);
  const [celestialData, setCelestialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchInFlightRef = useRef(false);

  // Get device geolocation once on mount.
  const detectLocation = useCallback(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ lat: position.coords.latitude, lon: position.coords.longitude });
        setLocationName("Your Location");
      },
      () => {
        // Keep default fallback — no harsh error to user.
      },
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 300000 }
    );
  }, []);

  // Fetch /celestial/data with the current location + the user's tz offset
  // and local time so the backend can resolve sundown precisely.
  //
  // 2026-02-21 hardening: automatic retry with exponential backoff on
  // transient failures (timeouts, 502/503/504, DNS blips during heavy
  // backend load like Motion Studio batch runs). Keeps the last-good
  // data visible while retrying silently instead of flipping the UI to
  // "Unable to fetch astronomical data" on a single stuttered request.
  const fetchCelestial = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    const token = localStorage.getItem("token");
    const now = new Date();
    const tzOffsetMinutes = now.getTimezoneOffset();
    const tzOffsetHours = -tzOffsetMinutes / 60;
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const localHour = now.getHours();
    const localMinute = now.getMinutes();

    const attempt = async () =>
      axios.get(`${API}/celestial/data`, {
        params: {
          latitude: location.lat,
          longitude: location.lon,
          tz_offset_hours: tzOffsetHours,
          local_date: localDate,
          local_hour: localHour,
          local_minute: localMinute,
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 15000,
      });

    const RETRY_DELAYS = [800, 2400, 6000]; // ms — total ~9s before giving up
    let lastErr = null;
    for (let i = 0; i <= RETRY_DELAYS.length; i++) {
      try {
        const res = await attempt();
        setCelestialData(res.data);
        setLastUpdated(Date.now());
        setError(null);
        fetchInFlightRef.current = false;
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // Don't retry on auth failure — user needs to log in again
        if (status === 401 || status === 403) break;
        // Retry on network errors + 5xx
        if (i < RETRY_DELAYS.length) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[i]));
          continue;
        }
      }
    }

    // Only surface the error if we don't have a previous good payload
    // to keep showing — otherwise the UI silently reuses cached data
    // while the poller tries again on the next tick.
    setCelestialData((prev) => {
      if (!prev) {
        const raw = lastErr?.response?.data?.detail;
        const readable =
          typeof raw === "string"
            ? raw
            : Array.isArray(raw)
              ? raw.map((d) => d?.msg || JSON.stringify(d)).join("; ")
              : lastErr?.message || "Unable to fetch astronomical data";
        setError(readable);
      }
      return prev;
    });
    setLoading(false);
    fetchInFlightRef.current = false;
  }, [location]);

  useEffect(() => {
    detectLocation();
  }, [detectLocation]);

  // Trigger immediate fetch when location resolves, then poll.
  // Stable ref pattern keeps the interval body decoupled from fetch identity.
  const fetcherRef = useRef(fetchCelestial);
  useEffect(() => {
    fetcherRef.current = fetchCelestial;
  }, [fetchCelestial]);

  useEffect(() => {
    if (!location.lat || !location.lon) return;
    const fn = fetcherRef.current;
    if (fn) fn();
  }, [location]);

  useEffect(() => {
    const interval = setInterval(() => {
      const fn = fetcherRef.current;
      if (fn) fn();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  // ── 2026-02-21 RCA fix ────────────────────────────────────────
  // Iteration 35 testing report: provider mounts BEFORE login, fires
  // the first /api/celestial/data without a bearer token → 403 →
  // `error` set, `celestialData` stays null. After login, the
  // 1-minute poll eventually catches up, but the CelestialDashboard
  // consumer was rendering "Failed to load" in the gap and the
  // promise of working nav buttons was unreachable.
  //
  // Listen for storage events (login flow writes "token" to
  // localStorage) AND for an explicit "ylc:auth-changed" custom
  // event the login flow can dispatch, then trigger a refetch
  // immediately so the dashboard hydrates within ~200ms of login
  // instead of waiting up to a minute for the next poll.
  useEffect(() => {
    const handler = () => {
      const fn = fetcherRef.current;
      if (fn) fn();
    };
    window.addEventListener("storage", handler);
    window.addEventListener("ylc:auth-changed", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("ylc:auth-changed", handler);
    };
  }, []);

  // Re-fetch on tab visibility change so a returning user sees fresh data.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        const fn = fetcherRef.current;
        if (fn) fn();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const value = {
    celestialData,
    location,
    locationName,
    loading,
    error,
    lastUpdated,
    refresh: fetchCelestial,
    setManualLocation: (lat, lon, name) => {
      setLocation({ lat, lon });
      setLocationName(name || "Manual Location");
    },
  };

  return <CelestialContext.Provider value={value}>{children}</CelestialContext.Provider>;
}

export function useCelestial() {
  const ctx = useContext(CelestialContext);
  if (!ctx) {
    // Safe fallback so components rendered outside the provider don't crash.
    return {
      celestialData: null,
      location: DEFAULT_LOCATION,
      locationName: DEFAULT_LOCATION_NAME,
      loading: true,
      error: null,
      lastUpdated: null,
      refresh: () => {},
      setManualLocation: () => {},
    };
  }
  return ctx;
}

export default CelestialContext;
